import React, { useContext, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as $Auth from '@services/Auth'
import { Spinner } from 'react-bootstrap'
import LoginContext from '@contexts/Login'
import Logoprossed from '@resources/imgs/logo-proseed.webp'
import SMS from '@resources/imgs/sms.png'
import LOCK from '@resources/imgs/lock.png'
import '@resources/css/login.css'
import { FaEyeSlash, FaEye, FaUser, FaStethoscope, FaArrowRight } from 'react-icons/fa'
import DonorsContext from '@contexts/Donors'

type Props = {
  donorId?: string
}

const Login: React.FC<Props> = ({ donorId }) => {
  const { email, setEmail, password, setPassword, isLoading, setIsLoading, error, setError, setCodeExpiresAt } = useContext(LoginContext)
  const { pagination } = useContext(DonorsContext)

  const navigate = useNavigate()

  const [showPassword, setShowPassword] = useState(false)
  const [mobileTab, setMobileTab] = useState<'register' | 'login'>('register')

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    $Auth.securityCode(email, password).then(({ data }) => {
      setError(null)
      setCodeExpiresAt(data?.expires_at ?? null)
      const link = donorId ? `/security-code?doador=${donorId}` : '/security-code'
      navigate(link)
    }).catch(e => {
      if (e.response?.data?.message && e.response.data.message === 'E-mail não verificado.') {
        navigate('/verificar-email')
      }

      setError(e.response?.data?.message || 'Ocorreu um erro ao tentar realizar o login. Favor tentar novamente.')
    }).finally(() => setIsLoading(false))
  }

  const togglePasswordVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    setShowPassword(!showPassword)
  }

  const searchParamDonor = donorId ? `?doador=${donorId}` : ''

  const totalDonors = Math.floor((pagination.total ?? 250) / 10) * 10

  return (
    <div className="container">
      <div className={`login-modal mb-1 mb-xl-5 mobile-tab-active--${mobileTab}`}>
        <div className="login-modal__logo">
          <figure>
            <img src={Logoprossed} alt="Logo Pro-Seed" className="logo-prossed" />
          </figure>
        </div>
        <div className="login-tabs d-lg-none">
          <button
            type="button"
            className={`login-tabs__btn ${mobileTab === 'register' ? 'is-active' : ''}`}
            onClick={() => setMobileTab('register')}
          >
            Criar conta
          </button>
          <button
            type="button"
            className={`login-tabs__btn ${mobileTab === 'login' ? 'is-active' : ''}`}
            onClick={() => setMobileTab('login')}
          >
            Entrar
          </button>
        </div>
        <div className="row">
          <div className="col-12 col-lg-6 order-2 order-lg-1">
            <div className="left-side">
              <div className="text-area">
                <h2>Quero me cadastrar</h2>
                <p className="mt-0">Selecione seu perfil para começar:</p>
              </div>
              <div className="profile-cards">
                <Link to={`/cadastre-se${searchParamDonor}`} className="profile-card profile-card--patient">
                  <div className="profile-card__header">
                    <div className="profile-card__icon">
                      <FaUser />
                    </div>
                    <div className="profile-card__body">
                      <h3>Sou Paciente em Tratamento</h3>
                      <p>Explore e escolha entre +{totalDonors} doadores</p>
                    </div>
                  </div>
                  <span className="profile-card__cta">
                    Criar meu cadastro <FaArrowRight />
                  </span>
                </Link>
                <Link to={`/medico/cadastre-se${searchParamDonor}`} className="profile-card profile-card--doctor">
                  <div className="profile-card__header">
                    <div className="profile-card__icon">
                      <FaStethoscope />
                    </div>
                    <div className="profile-card__body">
                      <h3>Sou Médico(a) ou Clínica</h3>
                      <p>Cadastro apenas para orientação de pacientes</p>
                    </div>
                  </div>
                  <span className="profile-card__cta">
                    Cadastro profissional <FaArrowRight />
                  </span>
                </Link>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6 order-1 order-lg-2 d-flex flex-column align-items-stretch mb-4 mb-lg-0 right-side">
            {error && (
              <div className="alert alert-danger" role="alert">
                <strong>Atenção!</strong> {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="lg-w-auto">
              <div className="right-side__top">
                <div className="text-login">
                  <h2>Já tenho cadastro</h2>
                  <p className="text-login__subtitle">Acesse sua conta</p>
                </div>
                <div className="form-floating has-validation">
                  <div className="inputs d-flex justify-content-between">
                    <i className="sms-icon"><img src={SMS} alt="" /></i>
                    <input
                      type="email"
                      className={'form-control mb-3 custom-placeholder' + (error ? ' is-invalid' : '')}
                      id="email"
                      placeholder="Insira seu e-mail *"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <div className="inputs d-flex justify-content-between">
                    <i className="pw-icon"><img src={LOCK} alt="" /></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={'form-control mb-3 custom-placeholder' + (error ? ' is-invalid' : '')}
                      id="password"
                      placeholder="Insira sua senha *"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                    <button
                      type="button"
                      className={`btn eye-icon px-3 ${error ? 'mr-3' : ''}`}
                      onClick={(e) => togglePasswordVisibility(e)}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="closed-eye-icon" />
                      ) : (
                        <FaEye className="open-eye-icon" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="btn-and-lostpw text-right">
                <Link to="/recuperar-senha">Esqueceu a senha?</Link>
                <button
                  type="submit"
                  className="btn btn-primary btn-block"
                  disabled={isLoading}
                >
                    Acessar {isLoading && <Spinner animation="border" className="ml-2" size="sm" />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
